import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { LayoutDashboardIcon, ZapIcon, SmartphoneIcon, SettingsIcon, ArrowRightIcon } from 'lucide-react';
export function ThemesPage() {
  usePageSEO({
    canonical: '/themes',
    keywords: 'Shopify themes, custom Shopify themes, premium Shopify themes, ecommerce themes, Shopify theme development',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Themes',
      path: '/themes'
    }]),
    title: 'Themes | Design Flow Digitals',
    description: 'Browse premium Shopify themes by Design Flow Digitals. Fast, customizable, and conversion-optimized themes built for modern ecommerce brands.'
  });
  const features = [{
    icon: <LayoutDashboardIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1732" data-mp="3encp5hbhl28y" />,
    title: 'Elegant Layouts',
    desc: 'Premium, conversion-optimized designs that elevate your brand identity and build trust with customers.'
  }, {
    icon: <ZapIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1733" data-mp="nifiy8q9dst9" />,
    title: 'Fast Performance',
    desc: 'Clean, lightweight code ensuring lightning-fast load times to reduce bounce rates and improve SEO.'
  }, {
    icon: <SettingsIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1734" data-mp="x9s82skoa4vl" />,
    title: 'Flexible Customization',
    desc: 'Extensive theme settings allowing you to tailor every aspect of your store without touching code.'
  }, {
    icon: <SmartphoneIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1735" data-mp="4mz4m2fwerhw" />,
    title: 'Mobile First Design',
    desc: 'Flawless shopping experiences across all devices, capturing the growing mobile commerce market.'
  }];
  const upcomingThemes = [{
    name: 'Luxe',
    style: 'Minimal & High-End',
    gradient: 'from-stone-800 to-black'
  }, {
    name: 'Minimal Pro',
    style: 'Clean & Modern',
    gradient: 'from-slate-800 to-black'
  }, {
    name: 'Starter Flow',
    style: 'Versatile & Fast',
    gradient: 'from-zinc-800 to-black'
  }];
  return <div className="pt-32 pb-20" data-id="element-1736" data-mp="2221ukjzmta6v">
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-24" data-id="element-1737" data-mp="270mvklmpjxwd">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-[#D4AF37]/30" data-id="element-1738" data-mp="2byocazwnvl4o">
          <LayoutDashboardIcon className="w-4 h-4 text-[#D4AF37]" data-id="element-1739" data-mp="1ebz428bd8cad" />
          <span className="text-sm font-medium text-[#F3E5AB]" data-id="element-1740" data-mp="1j9hczypdurid">
            Shopify Themes
          </span>
        </div>
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-1741" data-mp="1o7spyiaz5i4t">Premium Themes</h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed max-w-3xl mx-auto" data-id="element-1742" data-mp="1t02atjx8fs08">
          Designed for modern ecommerce brands. Clean code. Lightning speed.
          Built to convert.
        </p>
      </section>

      {/* Philosophy / Features */}
      <section className="section-padding bg-black border-y border-white/5" data-id="element-1743" data-mp="10h53ms0bapyq">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1744" data-mp="3dszhhpy00rx5">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1745" data-mp="39reaumc259ve">
            Our Theme Philosophy
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1746" data-mp="34s1jppe5lgn0">
            We build themes that don't just look beautiful, but are engineered
            to maximize your store's performance and sales.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1747" data-mp="2uvgfh69yk9k8">
          {features.map((feature, index) => <div key={index} className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-colors duration-300" data-id="element-1748" data-mp="2q1u2fczvu3nw" data-mpi={index}>
              <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6" data-id="element-1749" data-mp="2h4jt3x6mkolm">
                {feature.icon}
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-1750" data-mp="275caauh4g1r4">
                {feature.title}
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1751" data-mp="b1o4u97hfh8c">{feature.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Coming Soon */}
      <section className="section-padding" data-id="element-1752" data-mp="g2vg4i8u2yjr">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1753" data-mp="1b3awz2r70vr">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1754" data-mp="6b7nwf4gddh8">
            Coming Soon to the Theme Store
          </h2>
          <p className="text-xl text-gray-400 mb-8" data-id="element-1755" data-mp="3ogkrkbtj54yy">
            Our themes are designed to meet the strict standards of the Shopify
            Theme Marketplace.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1756" data-mp="3ej69y8vvea52">
          {upcomingThemes.map((theme, index) => <div key={index} className="glass-card overflow-hidden border-white/10 group" data-id="element-1757" data-mp="3ji9i4275cwh9" data-mpi={index}>
              <div className={`aspect-[4/3] bg-gradient-to-br ${theme.gradient} relative flex items-center justify-center border-b border-white/5`} data-id="element-1758" data-mp="1cpguhrqh4u5z">
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay" data-id="element-1759" data-mp="328or5jm1aq2u" />
                <div className="text-center z-10" data-id="element-1760" data-mp="3bxymd8c6cov6">
                  <p className="text-[#D4AF37] font-medium tracking-widest uppercase text-sm mb-2" data-id="element-1761" data-mp="3bzt98s70ar1w">
                    Preview
                  </p>
                  <p className="text-white/50 font-bold text-2xl" data-id="element-1762" data-mp="3kydjr62exuf1">
                    Coming Soon
                  </p>
                </div>
              </div>
              <div className="p-6" data-id="element-1763" data-mp="3gze6my6j2rci">
                <div className="flex items-center justify-between mb-1" data-id="element-1764" data-mp="3uxma7oy2kqwn">
                  <h3 className="text-xl font-bold text-white" data-id="element-1765" data-mp="3uuqapw6kpu63">{theme.name}</h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold uppercase tracking-wider" data-id="element-1766" data-mp="3pysstlfnwfr7">
                    <span className="w-2 h-2 rounded-full bg-[#D4AF37]" data-id="element-1767" data-mp="1vlta3jfzj5fp" />{' '}
                    Coming Soon
                  </span>
                </div>
                <p className="text-gray-400 text-sm" data-id="element-1768" data-mp="4ff0owo8putq">{theme.style}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center bg-gradient-to-b from-transparent to-[#D4AF37]/5" data-id="element-1769" data-mp="8jv14if34a1k">
        <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-1770" data-mp="de8t0aujfz1h">
          Want a Custom Theme?
        </h2>
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1771" data-mp="neswzl9fzhne">
          We build bespoke Shopify themes tailored specifically to your brand's
          unique requirements.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-black px-10 py-4 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 hover:scale-105" data-id="element-1772" data-mp="s7vid642fksi">
          Discuss Your Project <ArrowRightIcon className="w-5 h-5" data-id="element-1773" data-mp="165sczwyylh4j" />
        </Link>
      </section>
    </div>;
}